import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Search, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build the privacy-safe display label for a user. Prefers @username (the
// public handle), only falling back to real name for legacy accounts that
// never picked a username. Returns the string ready to render — including
// the `@` prefix — so callers don't have to remember to add it.
const displayHandle = (u) => {
  if (!u) return '';
  const uname = u.username;
  if (uname) return `@${uname}`;
  return u.display_name || u.name || 'skater';
};

const SendPostItDialog = ({ open, onOpenChange, preselectedUser = null }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(preselectedUser);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [searching, setSearching] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (preselectedUser) setSelectedUser(preselectedUser);
  }, [preselectedUser]);

  // Load all users alphabetically when dialog opens
  useEffect(() => {
    if (open && !preselectedUser) {
      setLoadingAll(true);
      const token = localStorage.getItem('token');
      axios.get(`${API}/users/search?q=__all__`, { headers: { Authorization: `Bearer ${token}` } })
        // Sort by what the user actually sees on screen (@username when
        // present, real name as legacy fallback). Mirrors the privacy
        // model on the backend.
        .then(({ data }) => setAllUsers(data.sort((a, b) => (a.display_name || a.username || a.name || '').localeCompare(b.display_name || b.username || b.name || ''))))
        .catch(() => {})
        .finally(() => setLoadingAll(false));
    }
  }, [open, preselectedUser]);

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSearchResults([]);
      setMessage('');
      if (!preselectedUser) setSelectedUser(null);
    }
  }, [open, preselectedUser]);

  const handleSearch = (q) => {
    setSearchQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) { setSearchResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/users/search?q=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleSend = async () => {
    if (!selectedUser || !message.trim()) return;
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/postit`, {
        to_user_id: selectedUser.user_id,
        message: message.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Post-it sent to ${displayHandle(selectedUser)}!`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send Post-it');
    } finally {
      setSending(false);
    }
  };

  const selectUser = (u) => {
    setSelectedUser(u);
    setSearchResults([]);
    setSearchQuery('');
  };

  const displayList = searchQuery.length >= 2 ? searchResults : allUsers;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg">Send a Post-it</DialogTitle>
        </DialogHeader>

        {!selectedUser ? (
          <div className="flex flex-col gap-3 min-h-0 flex-1">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by @username..."
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                className="pl-9"
                data-testid="postit-user-search"
                autoFocus
              />
              {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
            </div>

            {/* User list - scrollable alphabetical */}
            <div className="overflow-y-auto flex-1 min-h-0 max-h-[50vh] space-y-0.5">
              {loadingAll && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}
              {displayList.map(u => {
                const label = displayHandle(u);
                // Avatar fallback initial: strip the leading `@` so it
                // doesn't render as the first letter of the handle.
                const initial = (label.startsWith('@') ? label.slice(1) : label)[0] || '?';
                return (
                  <button
                    key={u.user_id}
                    onClick={() => selectUser(u)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/10 transition-colors text-left"
                    data-testid={`postit-user-option-${u.user_id}`}
                  >
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={u.photo || u.selfie_url || u.picture} />
                      <AvatarFallback className="text-xs bg-primary/20 text-primary font-bold">{initial.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                );
              })}
              {!loadingAll && searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6">No users found</p>
              )}
              {!loadingAll && searchQuery.length < 2 && allUsers.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6">No other users yet</p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Selected user */}
            <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
              <Avatar className="h-9 w-9">
                <AvatarImage src={selectedUser.photo || selectedUser.selfie_url || selectedUser.picture} />
                <AvatarFallback className="text-xs bg-primary/20 text-primary">
                  {((selectedUser.username || selectedUser.display_name || selectedUser.name || '?')[0] || '?').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-sm font-medium">To: {displayHandle(selectedUser)}</p>
              </div>
              {!preselectedUser && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)} className="text-xs">
                  Change
                </Button>
              )}
            </div>

            {/* Post-it preview with message */}
            <div className="relative mx-auto" style={{ transform: 'rotate(-1deg)' }}>
              <div
                className="relative p-4 rounded shadow-lg min-h-[100px]"
                style={{
                  background: 'linear-gradient(135deg, #fde68a 0%, #fcd34d 100%)',
                  boxShadow: '2px 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                <Textarea
                  placeholder="Write your message here..."
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  maxLength={300}
                  className="bg-transparent border-none resize-none text-amber-950 placeholder:text-amber-800/40 focus-visible:ring-0 p-0 text-sm"
                  style={{ fontFamily: "'Caveat', 'Segoe Script', cursive", fontSize: '16px' }}
                  rows={4}
                  data-testid="postit-message-input"
                  autoFocus
                />
                <p className="text-[10px] text-amber-900/40 text-right">{message.length}/300</p>
              </div>
            </div>

            <Button
              onClick={handleSend}
              disabled={sending || !message.trim()}
              className="w-full rounded-full"
              data-testid="postit-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Post-it
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendPostItDialog;
